// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { ThunkAction, ThunkDispatch } from 'utils/redux';

export enum LabelingRunsActionTypes {
    GET_RUNS = 'GET_RUNS',
    GET_RUNS_SUCCESS = 'GET_RUNS_SUCCESS',
    GET_RUNS_FAILED = 'GET_RUNS_FAILED',
    CREATE_RUN = 'CREATE_RUN',
    CREATE_RUN_SUCCESS = 'CREATE_RUN_SUCCESS',
    CREATE_RUN_FAILED = 'CREATE_RUN_FAILED',
    DELETE_RUN = 'DELETE_RUN',
    DELETE_RUN_SUCCESS = 'DELETE_RUN_SUCCESS',
    DELETE_RUN_FAILED = 'DELETE_RUN_FAILED',
    GET_RUN_DETAIL = 'GET_RUN_DETAIL',
    GET_RUN_DETAIL_SUCCESS = 'GET_RUN_DETAIL_SUCCESS',
    GET_RUN_DETAIL_FAILED = 'GET_RUN_DETAIL_FAILED',
    ADD_TASKS_TO_RUN = 'ADD_TASKS_TO_RUN',
    ADD_TASKS_TO_RUN_SUCCESS = 'ADD_TASKS_TO_RUN_SUCCESS',
    ADD_TASKS_TO_RUN_FAILED = 'ADD_TASKS_TO_RUN_FAILED',
    REMOVE_TASK_FROM_RUN = 'REMOVE_TASK_FROM_RUN',
    REMOVE_TASK_FROM_RUN_SUCCESS = 'REMOVE_TASK_FROM_RUN_SUCCESS',
    REMOVE_TASK_FROM_RUN_FAILED = 'REMOVE_TASK_FROM_RUN_FAILED',
    UPDATE_JOB_QA_STATE = 'UPDATE_JOB_QA_STATE',
    UPDATE_JOB_QA_STATE_SUCCESS = 'UPDATE_JOB_QA_STATE_SUCCESS',
    UPDATE_JOB_QA_STATE_FAILED = 'UPDATE_JOB_QA_STATE_FAILED',
    UPDATE_RUN = 'UPDATE_RUN',
    UPDATE_RUN_SUCCESS = 'UPDATE_RUN_SUCCESS',
    UPDATE_RUN_FAILED = 'UPDATE_RUN_FAILED',
    DUPLICATE_RUN = 'DUPLICATE_RUN',
    DUPLICATE_RUN_SUCCESS = 'DUPLICATE_RUN_SUCCESS',
    DUPLICATE_RUN_FAILED = 'DUPLICATE_RUN_FAILED',
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<any> {
    const response = await fetch(path, {
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') || '' },
        credentials: 'same-origin',
        ...options,
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(`${response.status}: ${text}`);
    }
    if (response.status === 204) return null;
    return response.json();
}

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(^|;\\s*)(${name})=([^;]*)`));
    return match ? decodeURIComponent(match[3]) : null;
}

export function getLabelingRunsAsync(): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.GET_RUNS });
        try {
            const data = await apiFetch('/api/labeling-runs?page_size=100');
            dispatch({
                type: LabelingRunsActionTypes.GET_RUNS_SUCCESS,
                payload: { runs: data.results || data, count: data.count || (data.results || data).length },
            });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.GET_RUNS_FAILED, payload: { error } });
        }
    };
}

export function createLabelingRunAsync(name: string): ThunkAction<Promise<any>> {
    return async (dispatch: ThunkDispatch): Promise<any> => {
        dispatch({ type: LabelingRunsActionTypes.CREATE_RUN });
        try {
            const data = await apiFetch('/api/labeling-runs', {
                method: 'POST',
                body: JSON.stringify({ name }),
            });
            dispatch({ type: LabelingRunsActionTypes.CREATE_RUN_SUCCESS, payload: { run: data } });
            return data;
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.CREATE_RUN_FAILED, payload: { error } });
            throw error;
        }
    };
}

export function deleteLabelingRunAsync(runId: number): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.DELETE_RUN, payload: { runId } });
        try {
            await apiFetch(`/api/labeling-runs/${runId}`, { method: 'DELETE' });
            dispatch({ type: LabelingRunsActionTypes.DELETE_RUN_SUCCESS, payload: { runId } });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.DELETE_RUN_FAILED, payload: { error } });
        }
    };
}

export function getRunDetailAsync(runId: number): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.GET_RUN_DETAIL });
        try {
            const [run, tasks, qaStates] = await Promise.all([
                apiFetch(`/api/labeling-runs/${runId}`),
                apiFetch(`/api/labeling-runs/${runId}/tasks`),
                apiFetch(`/api/labeling-runs/${runId}/qa-states`),
            ]);
            dispatch({
                type: LabelingRunsActionTypes.GET_RUN_DETAIL_SUCCESS,
                payload: { run, tasks, qaStates },
            });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.GET_RUN_DETAIL_FAILED, payload: { error } });
        }
    };
}

export function addTasksToRunAsync(runId: number, taskIds: number[]): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.ADD_TASKS_TO_RUN });
        try {
            const results = await Promise.all(
                taskIds.map((task_id) => apiFetch(`/api/labeling-runs/${runId}/tasks`, {
                    method: 'POST',
                    body: JSON.stringify({ task_id }),
                })),
            );
            dispatch({ type: LabelingRunsActionTypes.ADD_TASKS_TO_RUN_SUCCESS, payload: { tasks: results } });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.ADD_TASKS_TO_RUN_FAILED, payload: { error } });
            throw error;
        }
    };
}

export function removeTaskFromRunAsync(runId: number, taskId: number): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.REMOVE_TASK_FROM_RUN });
        try {
            await apiFetch(`/api/labeling-runs/${runId}/tasks/${taskId}`, { method: 'DELETE' });
            dispatch({ type: LabelingRunsActionTypes.REMOVE_TASK_FROM_RUN_SUCCESS, payload: { taskId } });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.REMOVE_TASK_FROM_RUN_FAILED, payload: { error } });
        }
    };
}

export function updateLabelingRunAsync(runId: number, name: string): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.UPDATE_RUN });
        try {
            const data = await apiFetch(`/api/labeling-runs/${runId}`, {
                method: 'PATCH',
                body: JSON.stringify({ name }),
            });
            dispatch({ type: LabelingRunsActionTypes.UPDATE_RUN_SUCCESS, payload: { runId, run: data } });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.UPDATE_RUN_FAILED, payload: { error } });
        }
    };
}

export function duplicateLabelingRunAsync(sourceRunId: number, newName: string): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.DUPLICATE_RUN });
        try {
            const newRun = await apiFetch('/api/labeling-runs', {
                method: 'POST',
                body: JSON.stringify({ name: newName }),
            });
            const tasks = await apiFetch(`/api/labeling-runs/${sourceRunId}/tasks`);
            await Promise.all(
                tasks.map((t: any) => apiFetch(`/api/labeling-runs/${newRun.id}/tasks`, {
                    method: 'POST',
                    body: JSON.stringify({ task_id: t.task_id }),
                })),
            );
            const fullRun = await apiFetch(`/api/labeling-runs/${newRun.id}`);
            dispatch({ type: LabelingRunsActionTypes.DUPLICATE_RUN_SUCCESS, payload: { run: fullRun } });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.DUPLICATE_RUN_FAILED, payload: { error } });
        }
    };
}

export function updateJobQAStateAsync(runId: number, jobId: number, state: string): ThunkAction {
    return async (dispatch: ThunkDispatch): Promise<void> => {
        dispatch({ type: LabelingRunsActionTypes.UPDATE_JOB_QA_STATE });
        try {
            const data = await apiFetch(`/api/labeling-runs/${runId}/qa-states/${jobId}`, {
                method: 'PUT',
                body: JSON.stringify({ state }),
            });
            dispatch({ type: LabelingRunsActionTypes.UPDATE_JOB_QA_STATE_SUCCESS, payload: { jobId, state: data.state } });
        } catch (error) {
            dispatch({ type: LabelingRunsActionTypes.UPDATE_JOB_QA_STATE_FAILED, payload: { error } });
        }
    };
}
